import React, { useEffect, useState } from "react";
import api from "../utils/api";
import "./Trash.css";
import { toast } from "react-toastify";


const Trash = () => {
    const [loading, setLoading] = useState(true);
    const [selectedItems, setSelectedItems] = useState([]);
    const [sortNewestFirst, setSortNewestFirst] = useState(true);
    const [viewMode, setViewMode] = useState("compact");
    const [confirmModal, setConfirmModal] = useState({
        open: false,
        action: null,
        message: "",
    });
    const [openMenu, setOpenMenu] = useState(null);
    const [sortBy, setSortBy] = useState("dateDeleted");

    const [trash, setTrash] = useState({
        notes: [],
        reminders: [],
        calendar: [],
        notifications: [],
    });

    useEffect(() => {
        loadTrash();
    }, []);

    useEffect(() => {
        const handleClickOutside = () => {
            setOpenMenu(null);
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    const loadTrash = async () => {
        try {
            setLoading(true);

            const [
                notesRes,
                remindersRes,
                calendarRes,
                notificationsRes,
            ] = await Promise.all([
                api.get("/api/trash/notes"),
                api.get("/api/trash/reminders"),
                api.get("/api/trash/calendar"),
                api.get("/api/trash/notifications"),
            ]);

            setTrash({
                notes: notesRes.data || [],
                reminders: remindersRes.data || [],
                calendar: calendarRes.data || [],
                notifications: notificationsRes.data || [],
            });
        } catch (err) {
            console.error("Failed to load trash:", err);
        } finally {
            setLoading(false);
        }
    };

    const getTitle = (item) =>
        item.title || item.name || item.message || item.text || item.content || "Untitled";

    const notifyLinkedModulesUpdated = () => {
        window.dispatchEvent(new Event("kuddle:notes-updated"));
        window.dispatchEvent(new Event("kuddle:reminders-updated"));
        window.dispatchEvent(new Event("kuddle:calendar-updated"));
        window.dispatchEvent(new Event("kuddle:notifications-updated"));
    };

    const handleRestore = async (group) => {
        try {
            await Promise.all(
                group.linkedItems.map((linked) =>
                    api.post(
                        `/api/trash/restore/${linked.trashType}/${linked._id}`
                    )
                )
            );

            await loadTrash();

            notifyLinkedModulesUpdated();


        } catch (err) {
            console.error("Restore failed:", err);
        }
    };

    const handlePermanentDelete = (group) => {
        openConfirmModal(
            "Permanently delete this item? This cannot be undone.",
            async () => {
                try {
                    await Promise.all(
                        group.linkedItems.map((linked) =>
                            api.delete(`/api/trash/permanent/${linked.trashType}/${linked._id}`)
                        )
                    );

                    await loadTrash();
                    closeConfirmModal();
                } catch (err) {
                    console.error("Permanent delete failed:", err);
                }
            }
        );
    };

    const handleEmptyTrash = () => {
        openConfirmModal(
            "Empty Recycle Bin? This will permanently delete all deleted notes, reminders, calendar items, and notifications. This cannot be undone.",
            async () => {
                try {
                    await api.delete("/api/trash/empty");
                    await loadTrash();
                    setSelectedItems([]);
                    closeConfirmModal();
                } catch (err) {
                    console.error("Empty recycle bin failed:", err);
                }
            }
        );
    };

    const handleSort = () => {
        setSortNewestFirst((prev) => !prev);
    };

    const handleViewToggle = () => {
        setViewMode((prev) => (prev === "compact" ? "details" : "compact"));
    };

    const handleCopySelected = async () => {
        if (selectedItems.length === 0) return;

        const copiedText = selectedItems
            .map((item) => {
                const deletedDate = item.deletedAt
                    ? new Date(item.deletedAt).toLocaleString()
                    : "Unknown";

                return `${item.itemType}: ${getTitle(item)}
Location: ${item.location}
Deleted: ${deletedDate}`;
            })
            .join("\n\n");

        try {
            await navigator.clipboard.writeText(copiedText);
        } catch (err) {
            console.error("Copy selected failed:", err);
        }
    };

    const handleRestoreAll = () => {
        if (allTrashItems.length === 0) return;

        openConfirmModal(
            "Restore all items in the Recycle Bin?",
            async () => {
                try {
                    await Promise.all(
                        groupedTrashItems.flatMap((group) =>
                            group.linkedItems.map((linked) =>
                                api.post(`/api/trash/restore/${linked.trashType}/${linked._id}`)
                            )
                        )
                    );

                    await loadTrash();
                    notifyLinkedModulesUpdated();
                    window.dispatchEvent(new Event("kuddle:notes-updated"));
                    window.dispatchEvent(new Event("kuddle:reminders-updated"));
                    window.dispatchEvent(new Event("kuddle:calendar-updated"));
                    window.dispatchEvent(new Event("kuddle:notifications-updated"));
                    setSelectedItems([]);
                    closeConfirmModal();
                } catch (err) {
                    console.error("Restore all failed:", err);
                }
            }
        );
    };

    const allTrashItems = [
        ...trash.notes.map((item) => ({ ...item, trashType: "note", itemType: "Note", location: "Notepad" })),
        ...trash.reminders.map((item) => ({ ...item, trashType: "reminder", itemType: "Reminder", location: "Reminders" })),
        ...trash.calendar.map((item) => ({ ...item, trashType: "calendar", itemType: "Calendar", location: "Calendar" })),
        ...trash.notifications.map((item) => ({ ...item, trashType: "notification", itemType: "Notification", location: "Notifications" })),
    ].sort((a, b) => {
        if (sortBy === "name") {
            return getTitle(a).localeCompare(getTitle(b));
        }

        if (sortBy === "location") {
            return a.location.localeCompare(b.location);
        }

        if (sortBy === "itemType") {
            return a.itemType.localeCompare(b.itemType);
        }

        const aDate = new Date(a.deletedAt || 0);
        const bDate = new Date(b.deletedAt || 0);

        return sortNewestFirst
            ? bDate - aDate
            : aDate - bDate;
    });

    const groupedTrashMap = {};

    const getUnifiedGroupKey = (item) => {
        if (item.trashType === "note") {
            return String(item._id);
        }

        return (
            item.fromNoteId ||
            item.meta?.fromNoteId ||
            item.noteId ||
            item.meta?.noteId ||
            item.linkedNoteId ||
            item._id
        );
    };

    allTrashItems.forEach((item) => {
        const key = getUnifiedGroupKey(item);

        if (!groupedTrashMap[key]) {
            groupedTrashMap[key] = {
                ...item,
                linkedItems: [],
                linkedTypes: new Set(),
                locations: new Set(),
            };
        }

        groupedTrashMap[key].linkedItems.push(item);
        groupedTrashMap[key].linkedTypes.add(item.itemType);
        groupedTrashMap[key].locations.add(item.location);
    });

    const groupedTrashItems = Object.values(groupedTrashMap).map((group) => ({
        ...group,
        linkedTypes: Array.from(group.linkedTypes),
        locations: Array.from(group.locations),
    }));

    const getGroupId = (item) => getUnifiedGroupKey(item);

    const isSelected = (item) =>
        selectedItems.some(
            (selected) => getGroupId(selected) === getGroupId(item)
        );

    const toggleSelectItem = (item) => {
        setSelectedItems((prev) =>
            isSelected(item)
                ? prev.filter(
                    (selected) => getGroupId(selected) !== getGroupId(item)
                )
                : [...prev, item]
        );
    };

    const toggleSelectAll = () => {
        if (selectedItems.length === groupedTrashItems.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(groupedTrashItems);
        }
    };

    const handleRestoreSelected = async () => {
        if (selectedItems.length === 0) return;

        try {
            await Promise.all(
                selectedItems.flatMap((group) =>
                    group.linkedItems.map((linked) =>
                        api.post(`/api/trash/restore/${linked.trashType}/${linked._id}`)
                    )
                )
            );

            await loadTrash();
            notifyLinkedModulesUpdated();
            window.dispatchEvent(new Event("kuddle:notes-updated"));
            window.dispatchEvent(new Event("kuddle:reminders-updated"));
            window.dispatchEvent(new Event("kuddle:calendar-updated"));
            window.dispatchEvent(new Event("kuddle:notifications-updated"));
            setSelectedItems([]);
        } catch (err) {
            console.error("Restore selected failed:", err);
        }
    };

    const openConfirmModal = (message, action) => {
        setConfirmModal({
            open: true,
            message,
            action,
        });
    };

    const closeConfirmModal = () => {
        setConfirmModal({
            open: false,
            action: null,
            message: "",
        });
    };

    const handleDeleteSelected = () => {
        if (selectedItems.length === 0) return;

        openConfirmModal(
            "Permanently delete selected items? This cannot be undone.",
            async () => {
                try {
                    await Promise.all(
                        selectedItems.flatMap((group) =>
                            group.linkedItems.map((linked) =>
                                api.delete(`/api/trash/permanent/${linked.trashType}/${linked._id}`)
                            )
                        )
                    );

                    setSelectedItems([]);
                    await loadTrash();
                    closeConfirmModal();
                } catch (err) {
                    console.error("Delete selected failed:", err);
                }
            }
        );
    };

    const handleShareSelected = async () => {
        if (selectedItems.length === 0) return;

        const shareText = selectedItems
            .map((item) => {
                const deletedDate = item.deletedAt
                    ? new Date(item.deletedAt).toLocaleString()
                    : "Unknown";

                return `Kuddlecore Recycle Bin Item

Name: ${getTitle(item)}
Type: ${item.itemType}
Location: ${item.location}
Deleted: ${deletedDate}`;
            })
            .join("\n\n-------------------\n\n");

        try {
            if (navigator.share) {
                await navigator.share({
                    title: "Kuddlecore Recycle Bin",
                    text: shareText,
                });
            } else {
                await navigator.clipboard.writeText(shareText);

                openConfirmModal(
                    "Share is not available on this device. Item details were copied to clipboard instead.",
                    () => closeConfirmModal()
                );
            }
        } catch (err) {
            console.error("Share failed:", err);
        }
    };

    const hasSelection = selectedItems.length > 0;
    const hasTrashItems = allTrashItems.length > 0;
    const invertSelection = () => {
        const inverted = groupedTrashItems.filter((item) => !isSelected(item));
        setSelectedItems(inverted);
    };

    return (
        <div className="trash-page">
            <div className="trash-header">
                <div>
                    <h1>🗑 Recycle Bin</h1>
                    <p>Review, restore, or permanently remove deleted Kuddlecore items.</p>
                </div>

                <div className="recycle-toolbar">
                    <button className="toolbar-btn" onClick={handleRestoreSelected} disabled={!hasSelection}>
                        Restore Selected
                    </button>

                    <button className="toolbar-btn" onClick={handleDeleteSelected} disabled={!hasSelection}>
                        Delete Selected
                    </button>

                    <button className="toolbar-btn" onClick={handleShareSelected} disabled={!hasSelection}>
                        Share
                    </button>

                    <div className="toolbar-menu-wrap" onClick={(e) => e.stopPropagation()}>
                        <button className="toolbar-btn" onClick={() => setOpenMenu(openMenu === "sort" ? null : "sort")} disabled={!hasTrashItems}>
                            Sort ▾
                        </button>

                        {openMenu === "sort" && (
                            <div className="toolbar-dropdown">
                                <button onClick={() => { setSortBy("name"); setOpenMenu(null); }}>Name</button>
                                <button onClick={() => { setSortBy("location"); setOpenMenu(null); }}>Location</button>
                                <button onClick={() => { setSortBy("dateDeleted"); setOpenMenu(null); }}>Date Deleted</button>
                                <button onClick={() => { setSortBy("itemType"); setOpenMenu(null); }}>Item Type</button>
                                <button onClick={() => { setSortNewestFirst(true); setOpenMenu(null); }}>Newest First</button>
                                <button onClick={() => { setSortNewestFirst(false); setOpenMenu(null); }}>Oldest First</button>
                            </div>
                        )}
                    </div>

                    <div className="toolbar-menu-wrap" onClick={(e) => e.stopPropagation()}>
                        <button className="toolbar-btn" onClick={() => setOpenMenu(openMenu === "view" ? null : "view")} disabled={!hasTrashItems}>
                            View ▾
                        </button>

                        {openMenu === "view" && (
                            <div className="toolbar-dropdown">
                                <button onClick={() => { setViewMode("details"); setOpenMenu(null); }}>Details</button>
                                <button onClick={() => { setViewMode("compact"); setOpenMenu(null); }}>Compact</button>
                            </div>
                        )}
                    </div>

                    <button className="toolbar-btn danger-toolbar" onClick={handleEmptyTrash} disabled={!hasTrashItems}>
                        Empty Recycle Bin
                    </button>

                    <button className="toolbar-btn" onClick={handleRestoreAll} disabled={!hasTrashItems}>
                        Restore All Items
                    </button>

                    <div className="toolbar-menu-wrap more-menu-wrap" onClick={(e) => e.stopPropagation()}>
                        <button className="toolbar-btn" onClick={() => setOpenMenu(openMenu === "more" ? null : "more")}>
                            ⋯
                        </button>

                        {openMenu === "more" && (
                            <div className="toolbar-dropdown more-dropdown">
                                <button onClick={() => { setSelectedItems(groupedTrashItems); setOpenMenu(null); }}>Select All</button>
                                <button onClick={() => { setSelectedItems([]); setOpenMenu(null); }}>Select None</button>
                                <button onClick={() => { invertSelection(); setOpenMenu(null); }}>Invert Selection</button>
                                <button onClick={() => { handleCopySelected(); setOpenMenu(null); }}>Copy Details</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="trash-empty">Loading...</div>
            ) : (
                <div className={`recycle-table-card ${viewMode === "details" ? "details-view" : "compact-view"}`}>
                    <table className="recycle-table">
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        checked={groupedTrashItems.length > 0 && selectedItems.length === groupedTrashItems.length}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th>Name</th>
                                <th>Original Location</th>
                                <th>Date Deleted</th>
                                <th>Linked Items</th>
                                <th>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {groupedTrashItems.map((item) => (
                                <tr key={`${item.trashType}-${item._id}`}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={isSelected(item)}
                                            onChange={() => toggleSelectItem(item)}
                                        />
                                    </td>
                                    <td>{getTitle(item)}</td>
                                    <td>{item.locations.join(", ")}</td>
                                    <td>{item.deletedAt ? new Date(item.deletedAt).toLocaleString() : "Unknown"}</td>
                                    <td>
                                        {item.linkedTypes.join(", ")}
                                    </td>
                                    <td className="trash-actions">
                                        <button
                                            type="button"
                                            onClick={() => handleRestore(item)}
                                        >
                                            Restore
                                        </button>

                                        <button
                                            type="button"
                                            className="danger"
                                            onClick={() => handlePermanentDelete(item)}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {confirmModal.open && (
                <div className="kc-modal-overlay">
                    <div className="kc-modal">
                        <h3>Confirm Action</h3>
                        <p>{confirmModal.message}</p>

                        <div className="kc-modal-actions">
                            <button className="kc-cancel-btn" onClick={closeConfirmModal}>
                                Cancel
                            </button>

                            <button className="kc-confirm-btn" onClick={confirmModal.action}>
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Trash;